/**
 * The active location's business profile in the portal
 * (openspec/changes/location-business-profile, D10/D11): what its staff and
 * services are called and whether every bill line needs a staff member. The
 * API resolves it; the portal keeps a copy with the shop details and refreshes
 * it on every dashboard load.
 */
import { BusinessProfile, StaffPerLine } from '../gql/graphql';

export type { BusinessProfile };
export { StaffPerLine };

export const PROFILE_STORAGE_KEY = 'businessProfile';

/**
 * The portal's behaviour from before profiles existed, for a session that has
 * nothing stored yet (a login from before the deploy, until the layout's query
 * answers): a stylist on every line, services on unless the stored kind is a
 * restaurant. A replay of the old rules, not a second copy of the presets.
 */
export function fallbackProfile(businessType: string | null | undefined): BusinessProfile {
	return {
		staffPerLine: StaffPerLine.Required,
		staffLabel: 'Stylist',
		servicesEnabled: businessType !== 'FOOD_DELIVERY',
		servicesLabel: 'Services',
	};
}

const isProfile = (value: unknown): value is BusinessProfile =>
	!!value && typeof value === 'object'
	&& Object.values(StaffPerLine).includes((value as BusinessProfile).staffPerLine)
	&& typeof (value as BusinessProfile).staffLabel === 'string'
	&& typeof (value as BusinessProfile).servicesEnabled === 'boolean'
	&& typeof (value as BusinessProfile).servicesLabel === 'string';

/** The stored profile when it is readable, else the fallback for the stored kind. */
export function profileFromStorage(stored: string | null | undefined, businessType: string | null | undefined): BusinessProfile {
	if (stored) {
		try {
			const parsed = JSON.parse(stored);
			if (isProfile(parsed)) {
				const { staffPerLine, staffLabel, servicesEnabled, servicesLabel } = parsed;
				return { staffPerLine, staffLabel, servicesEnabled, servicesLabel };
			}
		} catch {
			/* unreadable: the fallback applies */
		}
	}
	return fallbackProfile(businessType);
}

export function readStoredProfile(): BusinessProfile {
	try {
		return profileFromStorage(localStorage.getItem(PROFILE_STORAGE_KEY), localStorage.getItem('businessType'));
	} catch {
		return fallbackProfile(null);
	}
}

export function writeStoredProfile(profile: BusinessProfile | null | undefined): void {
	try {
		if (profile) localStorage.setItem(PROFILE_STORAGE_KEY, JSON.stringify(profileFromStorage(JSON.stringify(profile), null)));
		else localStorage.removeItem(PROFILE_STORAGE_KEY);
	} catch {
		/* private mode: the fallback applies on the next read */
	}
}

export const sameProfile = (a: BusinessProfile | null | undefined, b: BusinessProfile | null | undefined): boolean =>
	!!a && !!b && a.staffPerLine === b.staffPerLine && a.staffLabel === b.staffLabel
	&& a.servicesEnabled === b.servicesEnabled && a.servicesLabel === b.servicesLabel;

type Line = { quantity?: number | null; stylistUserId?: unknown };

/**
 * The staff rule the order page applies before saving, in the API's words so
 * a cashier sees one sentence whichever layer caught it. Null when the order
 * passes.
 */
export function staffProblem(order: { products?: Line[] | null; services?: Line[] | null }, profile: BusinessProfile): string | null {
	if (profile.staffPerLine !== StaffPerLine.Required) return null;
	const lines = [...(order.products ?? []), ...(order.services ?? [])].filter((line) => (line.quantity ?? 0) > 0);
	const missing = lines.some((line) => !line.stylistUserId || String(line.stylistUserId).trim() === '');
	return missing ? `Choose a ${profile.staffLabel} on every line.` : null;
}

/** The location form's override controls, as the API expects them: every field named, null for Default or an empty label (D5). */
export interface OverrideForm {
	staffPerLine: StaffPerLine | '';
	staffLabel: string;
	servicesEnabled: 'on' | 'off' | '';
	servicesLabel: string;
}

export function overridesInput(form: OverrideForm) {
	return {
		staffPerLine: form.staffPerLine || null,
		staffLabel: form.staffLabel.trim() || null,
		servicesEnabled: form.servicesEnabled === '' ? null : form.servicesEnabled === 'on',
		servicesLabel: form.servicesLabel.trim() || null,
	};
}

export const STAFF_PER_LINE_LABELS: Record<StaffPerLine, string> = {
	[StaffPerLine.Required]: 'Required',
	[StaffPerLine.Optional]: 'Optional',
	[StaffPerLine.Off]: 'Off',
};
